import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Mail } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FAQ_DATA, replaceFAQVariables } from '@/content/faqs';
import { fireEvent } from '@/lib/analytics';

export default function SupportFaqDetail() {
  const { slug } = useParams<{ slug: string }>();
  const faq = FAQ_DATA.find((f) => f.id === slug);

  React.useEffect(() => {
    if (faq) {
      fireEvent('support_view', { route: 'faq', faq_id: faq.id, category: faq.category });
    }
  }, [faq]);

  if (!faq) {
    return <Navigate to="/support" replace />;
  }

  const answer = replaceFAQVariables(faq.answer, {});
  const relatedFaqs = FAQ_DATA.filter((f) => f.category === faq.category && f.id !== faq.id).slice(
    0,
    3,
  );

  return (
    <>
      <Helmet>
        <title>{faq.question} - Attain Domains Support</title>
        <meta name="description" content={answer.substring(0, 160)} />
        <link rel="canonical" href={`https://attaindomains.com/support/faq/${faq.id}`} />
      </Helmet>

      <div className="min-h-screen bg-background py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/support" className="hover:text-foreground transition-colors">
              Support
            </Link>
            <span>/</span>
            <span className="text-foreground">{faq.category}</span>
          </nav>

          {/* Back link */}
          <Link
            to="/support"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Support
          </Link>

          {/* FAQ Content */}
          <article>
            <header className="mb-8">
              <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm rounded-full mb-4">
                {faq.category}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{faq.question}</h1>
            </header>

            <Card className="p-8 mb-8">
              <div className="prose prose-lg max-w-none">
                <p className="text-foreground/90 leading-relaxed whitespace-pre-line">{answer}</p>
              </div>

              {faq.ctas && faq.ctas.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-8 pt-8 border-t border-border">
                  {faq.ctas.map((cta, idx) => (
                    <Link key={idx} to={cta.to}>
                      <Button variant={idx === 0 ? 'default' : 'outline'}>{cta.label}</Button>
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* Related FAQs */}
            {relatedFaqs.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Related Articles</h2>
                <div className="space-y-3">
                  {relatedFaqs.map((related) => (
                    <Link key={related.id} to={`/support/faq/${related.id}`}>
                      <Card className="p-4 hover:shadow-lg transition-all cursor-pointer hover:border-primary/50">
                        <h3 className="font-semibold">{related.question}</h3>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Still need help */}
            <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Didn't find what you need?</h3>
                    <p className="text-sm text-muted-foreground">
                      Get in touch with me
                    </p>
                  </div>
                </div>
                <Link to="/support/contact">
                  <Button>Contact Support</Button>
                </Link>
              </div>
            </Card>
          </article>
        </div>
      </div>
    </>
  );
}
